import {
  activeNavId,
  formatAuthScope,
  parseRoute,
  TOKEN_RENEWAL_SCOPE,
  VISIOUS_SCOPES,
  type SessionFacts,
  type SessionStatus,
} from '@visious/core';
import { fmt, text } from '../../strings';
import { navLabels } from '../../shell/model';
import type { ProblemCopy } from '../../api/problem';

/**
 * §8's sign-in page — the reasoning, with no DOM in it (R13). `useLogin.ts` owns
 * `location`, `history`, `sessionStorage` and the queries; `view.tsx` renders what this returns.
 *
 * The page has one job beyond starting the grant: **say which of five things is true**, because the
 * instance says all of them with the same `403` and only the body separates them (`auth/failure.ts`).
 * Core decides the state (`describeSession`); what is here is the wording, which R7 keeps on the
 * platform that renders it.
 */

export interface PermissionLine {
  readonly id: string;
  readonly title: string;
  readonly detail: string;
  /** The scope string as the instance's consent screen shows it. Shown small, for the curious. */
  readonly scope: string;
  /**
   * Only meaningful once there is a grant: `false` marks a permission this build asks for and the
   * stored token does not carry, which is the `under-scoped` state made visible per row.
   */
  readonly granted: boolean;
}

export function permissionLines(
  facts: SessionFacts,
  /** The renewal row appears when the option is chosen (signed out) or the grant already holds it. */
  includeRenewal: boolean,
): readonly PermissionLine[] {
  const words = text().login.permission;
  const byEndpoint: Readonly<
    Record<string, { readonly title: string; readonly detail: string } | undefined>
  > = {
    subscriptions: words.subscriptions,
    playlists: words.playlists,
    feed: words.feed,
    preferences: words.preferences,
    history: words.history,
    tokens: words.tokens,
  };
  const missing = new Set(facts.missingScopes.map(formatAuthScope));
  const scopes = includeRenewal ? [...VISIOUS_SCOPES, TOKEN_RENEWAL_SCOPE] : VISIOUS_SCOPES;
  return scopes.map((scope) => {
    const copy = byEndpoint[scope.endpoint];
    const formatted = formatAuthScope(scope);
    return {
      id: scope.endpoint,
      // A scope with no copy is a scope somebody added without a sentence: name it rather than hide it.
      title: copy?.title ?? scope.endpoint,
      detail: copy?.detail ?? words.undescribed,
      scope: formatted,
      // The renewal scope is never in `missingScopes` (it is optional, not required), so a held
      // grant answers through `canSelfRenew` rather than through the missing set.
      granted:
        facts.signedIn &&
        (scope.endpoint === 'tokens' ? facts.canSelfRenew : !missing.has(formatted)),
    };
  });
}

export type VerifyOutcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'checking' }
  /** The authenticated call worked. `subscriptions` is a count rather than a list — this is a check. */
  | { readonly kind: 'ok'; readonly subscriptions: number }
  | { readonly kind: 'failed'; readonly copy: ProblemCopy; readonly signedOut: boolean };

export interface LoginNotice {
  readonly tone: 'info' | 'caution' | 'error' | 'positive';
  readonly text: string;
  /** `true` for a notice that appears after an action, so a screen reader hears it (`Notice.live`). */
  readonly live: boolean;
}

export interface SessionReadout {
  readonly username: string | null;
  /** "in 27 days" — a value beside a label, never a sentence: see the note on `expiryTitle`. */
  readonly expiry: string | null;
  /**
   * The absolute date, for the `title`. Deliberately a separate field rather than interpolated: a
   * label/value pair cannot produce "Updated vor 2 Wochen" because it has no verb to mix. 4e fixed the
   * cause as well — every `Intl` call now takes the UI locale — and this shape is still the right one.
   */
  readonly expiryTitle: string | null;
  readonly granted: string | null;
  readonly grantedTitle: string | null;
}

export interface LoginView {
  readonly ready: boolean;
  readonly signedIn: boolean;
  readonly instanceHost: string | null;
  readonly headline: string;
  readonly explanation: string;
  readonly notice: LoginNotice | null;
  readonly permissions: readonly PermissionLine[];
  readonly session: SessionReadout | null;
  readonly verify: VerifyOutcome;
  /** The label the primary button takes, which is the action's name rather than its mechanism. */
  readonly signInLabel: string;
  /** Where signing in will return to, when the reader was sent here by a gate. */
  readonly returnHref: string | null;
  /** That destination's name, spelled as the nav spells it. */
  readonly returnLabel: string | null;
  /** The renewal option's position, rendered as the toggle beside the sign-in actions. */
  readonly autoRenew: boolean;
}

/**
 * The name of the page a gate sent the reader away from. Read back out of the href through
 * `parseRoute` + `activeNavId` rather than passed down as a second parameter, because the href is
 * already the one thing that survived the round trip through the instance — and a label threaded
 * alongside it is a label that can disagree with it.
 *
 * `null` for a destination the nav does not describe, where the honest phrasing is "where you were"
 * rather than a guess at a title the page has not loaded yet.
 */
export function returnLabel(href: string | null): string | null {
  if (href === null) return null;
  const id = activeNavId(parseRoute(href));
  return id === null ? null : navLabels()[id];
}

export interface LoginInput {
  readonly ready: boolean;
  readonly facts: SessionFacts;
  readonly grantedAtEpochSec: number | null;
  readonly nowEpochSec: number;
  readonly instanceHost: string | null;
  /**
   * The reader came back from the consent screen with **nothing**. §8's denial has no `error`
   * parameter and no `denied` flag — it is a bare redirect to the callback — so "we started a grant
   * and no token arrived" is the only evidence that they said no, and it must read as a normal
   * outcome rather than as a fault.
   */
  readonly denied: boolean;
  /**
   * The grant came back for an instance that is no longer the one the app is talking to. Storing it
   * would file another instance's credential under this one's key, and every call would then answer
   * `Invalid signature` — an error whose wording sends the reader looking for a tampering problem.
   */
  readonly instanceMismatch: { readonly granted: string; readonly current: string } | null;
  /**
   * A callback arrived carrying a token, and this tab never started a grant that could have produced
   * it. Refused rather than stored — see `PendingGrant.state` in `useLogin.ts` for what that link is.
   */
  readonly unsolicited: boolean;
  /**
   * The grant was fine and this browser would not keep it — a blocked or full storage context. Its own
   * arm because "sign in again" is useless advice for it (`storeGrant`).
   */
  readonly storeFailed: boolean;
  /**
   * Why the callback carried no usable grant, when it carried something. `'no-token'` is the denial;
   * `'empty-token'` is the instance sending the parameter with nothing in it, which is a different
   * sentence — without it that case renders as an ordinary first visit and says nothing at all.
   */
  readonly callbackProblem: 'no-token' | 'empty-token' | 'bad-url' | null;
  readonly returnHref: string | null;
  readonly verify: VerifyOutcome;
  /** The renewal option's position — the view renders the toggle, this decides the sixth row. */
  readonly autoRenew: boolean;
}

export function loginView(input: LoginInput): LoginView {
  const facts = input.facts;
  return {
    ready: input.ready,
    signedIn: facts.signedIn,
    instanceHost: input.instanceHost,
    headline: headlineFor(input),
    explanation: explanationFor(input),
    notice: noticeFor(input),
    permissions: permissionLines(facts, input.autoRenew || facts.canSelfRenew),
    // Only for a session that *is* one. An expired token's readout was computed here at first and
    // never rendered — the heading already says what happened, and "expires · 3 weeks ago" beside a
    // sign-in button is a fact nobody needs.
    session: facts.signedIn ? readoutFor(input) : null,
    verify: input.verify,
    signInLabel: signInLabelFor(facts.status),
    returnHref: input.returnHref,
    returnLabel: returnLabel(input.returnHref),
    autoRenew: input.autoRenew,
  };
}

function headlineFor(input: LoginInput): string {
  const words = text().login.headline;
  if (!input.ready) return words.checking;
  switch (input.facts.status) {
    case 'active':
    case 'under-scoped':
      return input.facts.username === null
        ? words.signedInAnonymous
        : words.signedInAs(input.facts.username);
    case 'expired':
      return words.expired;
    case 'unreadable':
      return words.unreadable;
    case 'signed-out':
      if (input.storeFailed) return words.storeFailed;
      if (!input.denied) return words.signedOut;
      return input.callbackProblem === 'empty-token' ? words.emptyToken : words.denied;
  }
}

function explanationFor(input: LoginInput): string {
  const words = text().login.explanation;
  const host = input.instanceHost ?? words.theInstance;
  switch (input.facts.status) {
    case 'active':
      return words.active(host);
    case 'under-scoped':
      return words.underScoped(host);
    case 'expired':
      return words.expired;
    case 'unreadable':
      return words.unreadable(host);
    case 'signed-out':
      return words.signedOut(host);
  }
}

function noticeFor(input: LoginInput): LoginNotice | null {
  // First, because it is the only notice that is about somebody else's attempt rather than the
  // reader's own, and because the reader should be told even if something else also went wrong.
  const words = text().login.notice;

  if (input.unsolicited) {
    return { tone: 'error', live: true, text: words.unsolicited };
  }

  if (input.instanceMismatch !== null) {
    return {
      tone: 'error',
      live: true,
      text: words.instanceMismatch(input.instanceMismatch.granted, input.instanceMismatch.current),
    };
  }

  if (input.storeFailed) {
    return { tone: 'error', live: true, text: words.storeFailed };
  }

  if (input.verify.kind === 'failed') {
    return {
      tone: 'error',
      live: true,
      text: words.verifyFailed(input.verify.copy.title, input.verify.copy.detail),
    };
  }

  if (input.denied && !input.facts.signedIn) {
    return {
      tone: 'info',
      live: true,
      text: input.callbackProblem === 'empty-token' ? words.emptyToken : words.denied,
    };
  }

  if (input.facts.status === 'under-scoped') {
    return {
      tone: 'caution',
      live: false,
      text: words.underScoped(input.facts.missingScopes.length, VISIOUS_SCOPES.length),
    };
  }

  if (input.facts.expiringSoon) {
    return { tone: 'caution', live: false, text: words.expiringSoon };
  }

  /*
   * A *successful* verification deliberately does not produce a notice. It has its own line inside the
   * session panel, beside the facts it is about — and a browser showed why that is not a style
   * preference: with both, the page said "the instance accepted the token" twice in two wordings, once
   * at the top and once in the panel, and a screen reader read both. The banner is for things that need
   * attention; "it works" belongs next to the session it describes.
   */
  return null;
}

function readoutFor(input: LoginInput): SessionReadout {
  const facts = input.facts;
  return {
    username: facts.username,
    expiry: fmt().published(facts.expireEpochSec, input.nowEpochSec),
    expiryTitle: fmt().absoluteDate(facts.expireEpochSec),
    granted: fmt().published(input.grantedAtEpochSec, input.nowEpochSec),
    grantedTitle: fmt().absoluteDate(input.grantedAtEpochSec),
  };
}

function signInLabelFor(status: SessionStatus): string {
  const words = text().login.signIn;
  switch (status) {
    case 'signed-out':
      return words.signedOut;
    case 'expired':
      return words.expired;
    case 'unreadable':
      return words.unreadable;
    case 'under-scoped':
      return words.underScoped;
    case 'active':
      return words.active;
  }
}
